// Fetch wrapper that attaches `Authorization: Bearer <id_token>` from the
// current Cognito session.
//
// Reference: PRPs/phone_lookup_rtb_api_prp_v2.md §13.5 (REST contract).
//
// The wrapper is intentionally tiny — no react-query, no swr. Pages call
// it from a useEffect + useState, which keeps the bundle small and lets
// tests `vi.mock("../lib/api", ...)` cleanly.
//
// Anti-patterns explicitly avoided here:
//   - No swallowing of non-2xx — the helpers throw an `ApiError` so callers
//     can show the message rather than silently rendering an empty chart.
//   - No reading an api_key from storage. Authn is the Cognito JWT only.

import {
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";

import { getPool } from "./auth";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function getApiBase(): string {
  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const base = env["VITE_API_BASE"];
  if (!base) {
    throw new Error("VITE_API_BASE not configured");
  }
  return base.replace(/\/+$/, "");
}

async function getIdTokenFromPool(pool: CognitoUserPool): Promise<string | null> {
  const cu = pool.getCurrentUser();
  if (!cu) return null;
  return await new Promise<string | null>((resolve) => {
    cu.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });
  });
}

export interface ApiOptions extends Omit<RequestInit, "body"> {
  /** Optional JSON body — will be stringified and the content type set. */
  body?: unknown;
  /** Override id token (for tests). */
  idToken?: string | null;
}

async function apiRequest<T>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const base = getApiBase();
  const url = path.startsWith("http") ? path : `${base}${path}`;

  const headers = new Headers(opts.headers ?? {});
  if (opts.body !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  headers.set("accept", "application/json");

  let token = opts.idToken ?? null;
  if (token === null) {
    try {
      token = await getIdTokenFromPool(getPool());
    } catch {
      token = null;
    }
  }
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
    credentials: opts.credentials ?? "omit",
  };
  if (opts.body !== undefined) {
    init.body = typeof opts.body === "string"
      ? opts.body
      : JSON.stringify(opts.body);
  }

  const res = await fetch(url, init);

  // Try to parse JSON for both success + error.
  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const body = parsed as
      | { error?: string; message?: string; detail?: string }
      | null;
    const base = body?.error ?? body?.message ?? res.statusText;
    const msg = body?.detail ? `${base}: ${body.detail}` : base;
    throw new ApiError(res.status, parsed, msg);
  }

  return parsed as T;
}

// ---------------------------------------------------------------------------
// Typed API surface — mirrors the 8 routes in admin-api §13.5.
// ---------------------------------------------------------------------------

export interface ClientListItem {
  client_id: string;
  client_name: string;
  active: boolean;
  full_price: string;
  partial_price: string;
  partial_match_ratio: number;
  rps_cap: number;
  created_at: string;
  updated_at: string;
}

export interface Stats7d {
  decisionReasonCounts: Array<{ reason: string; count: number }>;
  latencyMs: { p50: number | null; p95: number | null; p99: number | null };
}

export interface ClientDetailResponse extends ClientListItem {
  stats7d?: Stats7d;
}

export interface ClientCreateRequest {
  client_name: string;
  full_price: string;
  partial_price: string;
  partial_match_ratio: number;
  rps_cap: number;
}

export interface ClientCreateResponse {
  client_id: string;
  client_name: string;
  /** Plaintext API key — shown to the operator exactly once. Never persist. */
  api_key_plaintext: string;
}

export interface ClientPatchRequest {
  active?: boolean;
  full_price?: string;
  partial_price?: string;
  partial_match_ratio?: number;
  rps_cap?: number;
}

export interface RotateKeyResponse {
  api_key_plaintext: string;
}

export interface DashboardResponse {
  rps: number;
  /** Service error rate (upstream timeouts/errors) — NOT business rejects. */
  errorRate: number;
  /** Fraction of requests we accepted (full or partial). */
  acceptRate: number;
  cacheHitRate: number;
  topReasons: Array<{ reason: string; count: number }>;
}

export interface HourlyPoint {
  ts: string;
  value: number | null;
}

export interface AcceptMixPoint {
  ts: string;
  full: number;
  partial: number;
}

export interface ClientStatsResponse {
  matchRateHourly: HourlyPoint[];
  acceptMixHourly: AcceptMixPoint[];
  cacheHitRateHourly: HourlyPoint[];
}

export interface ClientYieldResponse {
  configuredVsObserved: HourlyPoint[];
  streakDistribution: Array<{ status: string; count: number }>;
  hourlyVariance: HourlyPoint[];
}

// ---------------------------------------------------------------------------
// Mirror (Debt Lookup) types — mirror-client CRUD + dashboard. NO pricing
// fields: the mirror is a verbatim credit-pull pass-through (PRP v4 §6.5).
// Auth is via an `X-API-Key` minted exactly like the GTI Bearer key (shown
// once on create/rotate).
// ---------------------------------------------------------------------------

export interface MirrorClientListItem {
  client_id: string;
  client_name: string;
  active: boolean;
  rps_cap: number;
  created_at: string;
  updated_at: string;
}

export interface MirrorStats7d {
  totalCalls: number;
  cacheHitRate: number | null;
}

export interface MirrorClientDetailResponse extends MirrorClientListItem {
  stats7d?: MirrorStats7d;
}

export interface MirrorClientCreateRequest {
  client_name: string;
  rps_cap?: number;
}

export interface MirrorClientCreateResponse {
  client_id: string;
  /** Plaintext X-API-Key — shown to the operator exactly once. Never persist. */
  api_key_plaintext: string;
}

export interface MirrorClientPatchRequest {
  active?: boolean;
  rps_cap?: number;
}

export interface MirrorDashboardResponse {
  totalCalls: number;
  cacheHitRate: number;
  upstreamCalls: number;
  /** Fraction of UPSTREAM calls that failed (denominator = upstream calls). */
  upstreamErrorRate: number;
}

export type ErrorLogKind = "upstream" | "internal" | "all";

export interface ErrorLogEntry {
  timestamp: string;
  requestId: string;
  clientId: string;
  phoneHash: string;
  category: "upstream" | "internal";
  errorType: string;
  upstreamStatus: number | null;
  upstreamLatencyMs: number | null;
  totalLatencyMs: number | null;
}

export interface ErrorLogResponse {
  kind: ErrorLogKind;
  windowSec: number;
  count: number;
  /** True when the result hit the row limit (more may exist). */
  limited: boolean;
  entries: ErrorLogEntry[];
}

/** One request blocked before the handler (bad key, IP allowlist, …). */
export interface AccessDenialEntry {
  timestamp: string;
  ip: string;
  route: string;
  status: number;
  requestId: string;
  reason: string;
}

export interface AccessDenialsResponse {
  windowSec: number;
  count: number;
  /** True when the result hit the row limit (more may exist). */
  limited: boolean;
  entries: AccessDenialEntry[];
}

export interface LeadEntry {
  pulledAtMs: number | null;
  pulledAt: string | null;
  clientId: string | null;
  name: string;
  phone: string;
  phoneHash: string;
  address: string;
  debt: string | null;
  fico: string | null;
  utilization: string | null;
  match: string | null;
  /** true = served from cache, false = fresh upstream pull, null = rejected/unknown. */
  cacheHit: boolean | null;
  /** true = the call was rate-limited (429) before reaching the cache. */
  rejected: boolean;
  /** true = the lead's cache row carries a TrustedForm cert URL. */
  trustedForm: boolean;
  /** The lead's OWN TrustedForm cert URL when held (its per-request event row,
   *  or a historical fresh-pull join), else null. A bearer credential to the
   *  session replay — admin-only surface (Cognito+MFA; brief OQ3). */
  trustedFormUrl: string | null;
}

export interface LeadsResponse {
  count: number;
  windowStart: number;
  windowEnd: number;
  clientId: string;
  limited: boolean;
  entries: LeadEntry[];
}

/** Time selection: either a rolling window (seconds) or an explicit epoch range. */
export type TimeSel =
  | { kind: "window"; windowSec: number }
  | { kind: "range"; startSec: number; endSec: number };

export interface DebtMixResponse {
  total: number;
  counts: { below_10k: number; over_10k: number; over_20k: number };
  pct: { below_10k: number; over_10k: number; over_20k: number };
  clientId: string;
}

export type DebtTier = "over_20k" | "over_10k" | "below_10k";
export interface TierCode {
  code: number;
  range: string;
  tier: DebtTier;
}
export interface DebtTiersResponse {
  codes: TierCode[];
  source: "stored" | "default";
}

// ---------------------------------------------------------------------------
// SMS Attribution Engine types — campaigns CRUD, stats, runs, send-delay.
// Shapes mirror the admin-api SMS routes (Tasks 21–24) and the PRP
// Data models block (PRPs/sms_attribution_engine_prp.md lines 470–491).
// ---------------------------------------------------------------------------

/** Where the dispatcher sources candidate phones for a campaign.
 *  - "cache": the live credit-cache query (v1 behaviour, default).
 *  - "list": an operator-uploaded recipient list (CSV upload, MIS-964/965). */
export type SmsCampaignLeadSource = "cache" | "list";

export interface SmsCampaignListItem {
  campaign_id: string;
  campaign_name: string;
  vertical: string;
  state: "active" | "paused";
  /** "cache" (default) | "list" — list campaigns text an uploaded recipient list. */
  lead_source: SmsCampaignLeadSource;
  msgs_per_min: number;
  callback_did: string;
  /** When true, this campaign bypasses the 14-day cross-campaign resend guard. */
  skip_resend_guard?: boolean;
  created_at: string;
  updated_at: string;
}

export interface SmsCampaignDetailResponse extends SmsCampaignListItem {
  openers: string[]; // {n}/{p} placeholder strings, ordered
  openers_no_name: string[];
  actions: string[];
  ctas: string[];
  footer: string; // operator-editable opt-out suffix (plain text)
  resend_guard_ms: number;
  current_run_id?: string;
}

export interface SmsCampaignCreateRequest {
  campaign_name: string;
  vertical: string;
  /** Lead source for the new campaign. Defaults to "cache" server-side when omitted. */
  lead_source?: SmsCampaignLeadSource;
  callback_did: string;
  msgs_per_min: number;
  openers: string[];
  openers_no_name: string[];
  actions: string[];
  ctas: string[];
  footer?: string;
  resend_guard_ms?: number;
  /** Bypass the 14-day cross-campaign resend guard for this campaign. Default false. */
  skip_resend_guard?: boolean;
}

export interface SmsCampaignPatchRequest {
  campaign_name?: string;
  vertical?: string;
  lead_source?: SmsCampaignLeadSource;
  callback_did?: string;
  msgs_per_min?: number;
  state?: "active" | "paused";
  openers?: string[];
  openers_no_name?: string[];
  actions?: string[];
  ctas?: string[];
  footer?: string;
  resend_guard_ms?: number;
}

// A reusable rollup over a set of sends + their attributed calls.
// Rate fields are computed server-side; denominator is `delivered`, NOT `sends`.
export interface SmsRollup {
  sends: number;
  delivered: number;
  failed: number;
  skipped: number;
  attributedCalls: number;
  conversions: number;
  revenueCents: number;
  callRate: number; // attributedCalls / delivered
  conversionRate: number; // conversions / delivered
  revenuePerSend: number; // revenueCents / delivered
}

export interface SmsStatsResponse {
  totals: SmsRollup;
  byCampaign: Array<
    { campaign_id: string; campaign_name: string; vertical: string } & SmsRollup
  >;
  byVertical: Array<{ vertical: string } & SmsRollup>;
  byHourOfDay: Array<{ hourPt: number } & SmsRollup>; // hourPt 0..23 PT
  byDayOfWeek: Array<{ dowPt: number } & SmsRollup>; // dowPt 0..6 PT (Sun=0)
  overTime: Array<{ day: string } & SmsRollup>; // daily time-series
  lagBuckets: Array<{ label: string; count: number }>; // minutes-to-first-call histogram
  bestHourPt: number | null; // argmax revenuePerSend, min-sample guarded (e.g. >=20 delivered); null if none qualify
  /** The under-$10K Auto Send tier's daily VOLUME series (MIS-1553). This tier
   *  is deliberately excluded from `totals` and every other rollup above —
   *  calls/conversions/revenue are not tracked for it — so this is the ONLY
   *  place its send volume appears. Empty until the tier is enabled. */
  u10kSends: Array<{ day: string; sends: number; delivered: number }>;
}

export interface SmsRunReport {
  run_id: string;
  started_at_ms: number;
  ended_at_ms?: number;
  msgs_per_min_at_start: number;
  rollup: SmsRollup;
}

export interface SmsCampaignRunsResponse {
  campaign_id: string;
  runs: SmsRunReport[];
}

export interface SmsDelaySetting {
  sendDelayMs: number;
  source: "stored" | "default";
}

export interface SmsAutoDispatchSetting {
  enabled: boolean;
  source: string; // "stored" | "default"
}

// Singleton SMS Auto Send config (MIS-967) — the cache auto-sender's full
// config in one object: on/off, send-delay, rate, callback DID, and its own
// message copy. Mirrors the admin-api putAutoSendConfig body + rtb-api
// getAutoSendConfig shape (same object stored under "sms_auto_send_config").
/** One debt tier's complete Auto Send settings (MIS-1553). Both the over-$10K
 *  and under-$10K tiers carry this identical field set. */
export interface AutoSendTierConfig {
  enabled: boolean;
  send_delay_ms: number;
  msgs_per_min: number;
  callback_did: string;
  openers: string[];
  openers_no_name: string[];
  actions: string[];
  ctas: string[];
  footer: string;
  // Credit-cache client_ids whose leads are auto-texted (e.g. ["jim"]). Empty
  // = text nobody. Mirrors admin-api putAutoSendConfig + rtb-api getAutoSendConfig.
  allowed_client_ids: string[];
}

/** SHAPE NOTE: this object's OWN top-level fields ARE the over-$10K tier — the
 *  exact fields that existed before tiering, so the stored config needs no
 *  migration. The under-$10K tier (every lead under $10,000 INCLUDING those with
 *  no readable debt) is nested under `under_10k` and ships disabled.
 *
 *  The admin-api PUT body schema is `.strict()` and `under_10k` is REQUIRED —
 *  a save without it is a 400. */
export interface AutoSendConfig extends AutoSendTierConfig {
  under_10k: AutoSendTierConfig;
}

// The GET/PUT auto-send routes wrap the config in a { config, source } envelope.
// getAutoSendConfig/putAutoSendConfig return the bare object PLUS the source so
// the page can show stored-vs-default — matching the server's {config} shape
// (the v2-review unwrap-mismatch bug class is avoided by mirroring it exactly).
export interface AutoSendConfigResponse {
  config: AutoSendConfig;
  source: string; // "stored" | "default"
}

// CSV recipient list upload (MIS-964/965). The POST route accepts raw CSV text
// (or {csv:"..."}) and returns accept/reject counts; the GET ?summary route
// returns recipient counts by status. The hard cap is 100k rows per upload —
// rows beyond that are truncated (the server flags `truncated:true`).
export const SMS_RECIPIENT_UPLOAD_CAP = 100_000;

export interface SmsRecipientUploadResult {
  accepted: number;
  rejected: number;
  total: number;
  /** True when the 100k-row cap dropped rows from this upload. */
  truncated?: boolean;
}

export interface SmsRecipientSummary {
  pending: number;
  sent: number;
  failed: number;
  skipped: number;
  total: number;
}

// The GET ?summary route wraps the counts in a { campaign_id, counts } envelope;
// getSmsRecipientSummary unwraps it so callers get the bare counts.
interface SmsRecipientSummaryEnvelope {
  campaign_id: string;
  counts: SmsRecipientSummary;
}

// ---------------------------------------------------------------------------
// GRT Conversion Postback types — Reporting → GRT Postback table. Shapes
// mirror the admin-api list route (routes/grt_postbacks.ts): the stored
// DynamoDB item minus raw/raw_truncated (the verbatim webhook blob is
// stripped server-side), wrapped in the leads.ts-style envelope.
// ---------------------------------------------------------------------------

export interface GrtPostbackRecord {
  /** PK; "noid:<uuid>" when GRT sent a blank GRT_ID. */
  grt_id: string;
  received_at_day: string; // "2026-07-09" UTC
  received_at_ms: number;
  source: string; // "grt"
  first_name: string | null;
  last_name: string | null;
  state: string | null;
  city: string | null;
  phone_number: string | null;
  phone_digits: string | null;
  zip: string | null;
  address_line1: string | null;
  enrolled_date: string | null; // ISO YYYY-MM-DD
  enrolled_debt: number | null;
  agent: string | null;
  campaign: string | null;
  transferred_by: string | null;
  fp_date: string | null; // ISO YYYY-MM-DD
  rec_start_date: string | null; // ISO YYYY-MM-DD
}

export interface ListGrtPostbacksEnvelope {
  count: number;
  windowStart: number; // epoch SECONDS
  windowEnd: number; // epoch SECONDS
  campaign: string; // echoed filter, or "all"
  limited: boolean;
  entries: GrtPostbackRecord[];
}

// ---------------------------------------------------------------------------
// Dialer types — the TrackDrive callback-dialer admin surface (config +
// on/off + stats + recent dials). Shapes mirror the admin-api dialer routes
// (routes/dialer.ts). The DialConfig contract is an intentional COPY of
// rtb-api's config/dial_settings.ts (admin-api cannot import rtb-api), so
// these MUST stay in lockstep with the server. The dialer stays dark
// (enabled:false) by default — this surface only READS its dial.* logs + the
// td-dials ledger and WRITES its operator config singleton.
// ---------------------------------------------------------------------------

/** The debt tiers we are willing to dial (mirrors dialer.ts:DialTierFloor).
 *  "over_20k" = only over-$20k leads; "over_10k" = over_10k OR over_20k. */
export type DialTierFloor = "over_20k" | "over_10k";

/** The singleton TrackDrive callback-dialer config (mirrors dialer.ts:DialConfig).
 *  `wait_ms`/`max_age_ms` are a PAIR defining a narrow eligibility BAND; the PUT
 *  enforces `max_age_ms > wait_ms` (400 invalid_band) — the reader does not. */
export interface DialConfig {
  enabled: boolean;
  tier_floor: DialTierFloor;
  wait_ms: number;
  max_age_ms: number;
  calls_per_min: number;
}

/** GET /api/dialer/config → the merged config + the read-only display extras.
 *  `traffic_source_id`/`ad_account` come from the dial Lambda's Terraform env
 *  and may be ABSENT on the admin Lambda (→ null); `tick_cap` is the dispatcher's
 *  hard per-tick post ceiling (calls_per_min may rise to it, never past it).
 *  Mirrors dialer.ts:getDialerConfig's jsonResponse body. */
export interface DialerConfigResponse {
  config: DialConfig;
  tick_cap: number;
  traffic_source_id: string | null;
  ad_account: string | null;
}

/** GET /api/dialer/stats → the dial.* outcome funnel + skip breakdown + by-tier
 *  + latency + tick totals, windowed. Funnel/skips/totals counts default to 0
 *  server-side; only the latency percentiles are nullable (null = no samples).
 *  Mirrors dialer.ts:dialerStats's jsonResponse body. */
export interface DialerStats {
  window: { startMs: number; endMs: number };
  funnel: {
    posted: number;
    existing_lead: number;
    rejected_dnc: number;
    rejected_dupe: number;
    rejected_other: number;
    failed: number;
  };
  skips: {
    recent_dial: number;
    invalid_phone: number;
    called_already_local: number;
    called_already_live: number;
    probe_failed: number;
    claim_lost: number;
    lead_error: number;
  };
  by_tier: Array<{ tier: string; count: number }>;
  latency: {
    avg_ms: number | null;
    p50_ms: number | null;
    p95_ms: number | null;
  };
  totals: {
    considered: number;
    attempted: number;
    posted: number;
    existing: number;
    rejected: number;
    failed: number;
    skipped: number;
  };
}

/** One recent-dials row off the td-dials by-day GSI (mirrors dialer.ts:
 *  DialListEntry + mapDialRow). Phone is masked server-side; the name/tier
 *  fields are coerced to "" (NOT null) by the server's `str()` helper — so only
 *  `td_lead_id` is nullable here (NB: the task draft's string|null on the three
 *  name/tier fields was corrected to string to match mapDialRow exactly). */
export interface DialerDialRow {
  phone_masked: string;
  first_name: string;
  last_name: string;
  derived_tier: string;
  status: string;
  td_lead_id: string | null;
  dialed_at_ms: number;
}

// The config PUT + enabled routes wrap the saved config in a { config }
// envelope; the dials route wraps the list in { count, entries }. Both are
// unwrapped at the boundary below so callers get the bare DialConfig /
// DialerDialRow[] (matching getDialerConfig which is returned whole).
interface DialerConfigEnvelope {
  config: DialConfig;
}
interface ListDialsEnvelope {
  count: number;
  entries: DialerDialRow[];
}

// ---------------------------------------------------------------------------
// Lead Sales types — the lead-sale proxy admin surface (config incl. the
// destinations / routing-rules / exclusive-buyers arrays + on/off + stats +
// recent sales + a seen-buyers helper). Shapes mirror the admin-api lead-sales
// routes (routes/lead_sales.ts). The LeadSaleConfig contract is an intentional
// COPY of rtb-api's config/lead_sale_config.ts (admin-api cannot import
// rtb-api), so these MUST stay in lockstep with the server. The proxy ships
// dark (enabled:false) — this surface only READS its lead_sale.* logs + the
// lead-sales ledger and WRITES its operator config singleton. Every api-key is
// a Secrets Manager ARN reference (api_key_secret_arn) — never the key value.
// ---------------------------------------------------------------------------

/** The debt tiers a routing rule can target; "any" matches every tier. Mirrors
 *  lead_sales.ts:Tier (== rtb-api Tier) plus the routing "any" fallthrough. The
 *  dollar buckets over_30k / over_40k are derived at dispatch from the candidate's
 *  unsecuredDebt midpoint (not the shared product-code tier). */
export type LeadSaleRoutingTier =
  | "below_10k"
  | "over_10k"
  | "over_20k"
  | "over_30k"
  | "over_40k"
  | "any";

/** A LeadExec sell destination. `api_key_secret_arn` is a Secrets Manager ARN —
 *  the key VALUE is NEVER stored here (mirrors lead_sales.ts:SellDestination).
 *  The operator pastes a raw key into `api_key_new` (write-only, sent on PUT);
 *  admin-api writes it to Secrets Manager and returns only the ARN + a masked
 *  `api_key_hint`. `skip_if_called` excludes leads that have called TrackDrive
 *  since the form-fill. */
export interface SellDestination {
  id: string;
  name: string;
  enabled: boolean;
  url: string;
  /** Only buy leads that have NOT called TrackDrive since the form-fill.
   *  Defaults to false server-side when a stored destination predates this field. */
  skip_if_called: boolean;
  api_key_secret_arn: string;
  /** Masked, display-only hint of the stored key (e.g. "f4da…c266"). Safe to
   *  store + return; NEVER the full key. Absent for keys set before this field. */
  api_key_hint?: string;
  /** Write-only: the raw key the operator pastes. Sent on PUT only when a new key
   *  is being set/replaced; NEVER returned by GET, NEVER stored in the config. */
  api_key_new?: string;
  source: { vid: number; aid: number; lid: number };
}

/** A tier-weighted routing rule (mirrors lead_sales.ts:RoutingRule). A rule
 *  points at a destination_id and carries a non-negative weight; the dispatcher
 *  weighted-randomly picks among the rules eligible for the lead's tier. */
export interface RoutingRule {
  tier: LeadSaleRoutingTier;
  destination_id: string;
  weight: number;
}

/** The singleton lead-sale proxy config (mirrors lead_sales.ts:LeadSaleConfig).
 *  `wait_ms`/`max_age_ms` are a PAIR defining a narrow eligibility BAND; the PUT
 *  enforces `max_age_ms > wait_ms` (400 invalid_band) — the reader does not.
 *  `enabled` is owned by the master toggle, never a config Save. */
export interface LeadSaleConfig {
  enabled: boolean;
  wait_ms: number;
  max_age_ms: number;
  sales_per_min: number;
  destinations: SellDestination[];
  routing_rules: RoutingRule[];
  exclusive_buyer_ids: number[];
}

/** GET /api/lead-sales/config → the merged config + the SALE per-tick cap
 *  (sales_per_min may rise to it, never past it). Mirrors getLeadSalesConfig's
 *  jsonResponse body — returned whole (like dialerConfig). */
export interface LeadSaleConfigResponse {
  config: LeadSaleConfig;
  tick_cap: number;
}

/** GET /api/lead-sales/stats → UNIQUE-lead stats, windowed. `outcomes` are read
 *  from the lead-sales ledger (one row per phone, its final decision) and
 *  RECONCILE — they sum to "leads decided this window". `held_back.already_sold`
 *  is a SEPARATE do-not-resell tally (unique leads withheld from a re-sale — the
 *  mutex protecting revenue/contracts, NOT an outcome, not part of the outcome
 *  sum). `considered` is the unique count of leads the dispatcher evaluated;
 *  `transient_skips` are the non-final, retried skips (deduped per lead —
 *  blocked_exclusive / no_route are owned by `outcomes`, never repeated here).
 *  All counts default to 0 server-side. The `by_buyer` rows are keyed by numeric
 *  buyer_id only — the frontend joins the name via seenBuyers(). Mirrors
 *  leadSalesStats's jsonResponse body. */
export interface LeadSaleStats {
  window: { startMs: number; endMs: number };
  outcomes: {
    sold: number;
    rejected: number;
    no_route: number;
    blocked_exclusive: number;
    failed: number;
    // Witzzy-first sequential-fallback stage (PRP witzzy_sequential AC17) —
    // OPTIONAL so a response from before that stage shipped (no witzzy.* keys)
    // still renders: witzzy_sent = pending a fallback resolution, converted /
    // expired are terminal Witzzy-owned outcomes, send_failed is a `failed`
    // row whose destination_id is "witzzy" (kept out of the plain `failed`
    // bucket so the two failure populations don't blur).
    witzzy_sent?: number;
    witzzy_converted?: number;
    witzzy_expired?: number;
    witzzy_send_failed?: number;
  };
  held_back: { already_sold: number };
  considered: number;
  transient_skips: {
    invalid_phone: number;
    exclusive_check_failed: number;
    claim_lost: number;
    lead_error: number;
  };
  by_destination: Array<{ destination_id: string; count: number }>;
  by_buyer: Array<{ buyer_id: number; count: number }>;
}

/** One recent-sales row off the lead-sales by-day GSI (mirrors lead_sales.ts:
 *  SaleListEntry + mapSaleRow). Phone is masked server-side; name/tier/status
 *  are coerced to "" (NOT null); destination_id / leadexec_lead_id /
 *  exclusive_buyer_name / failure_reason are nullable (failure_reason is
 *  length-bounded server-side as it may echo LeadExec's raw reject text). */
export interface LeadSaleRow {
  phone_masked: string;
  first_name: string;
  last_name: string;
  tier: string;
  status: string;
  destination_id: string | null;
  leadexec_lead_id: string | number | null;
  exclusive_buyer_name: string | null;
  failure_reason: string | null;
  sold_at_ms: number;
}

/** A distinct (buyer_id, buyer_name) pair seen on recent td-calls, for the
 *  exclusive-buyer picker (mirrors lead_sales.ts:SeenBuyer). `sample_revenue_cents`
 *  is the max revenue seen for that buyer (a hint at who actually pays), or null
 *  when no priced call was seen. */
export interface SeenBuyer {
  buyer_id: number;
  buyer_name: string;
  sample_revenue_cents: number | null;
}

// The config PUT + enabled routes wrap the saved config in a { config }
// envelope; the sales route wraps the list in { count, entries }; seen-buyers
// wraps in { count, buyers }. All three are unwrapped at the boundary below so
// callers get the bare LeadSaleConfig / LeadSaleRow[] / SeenBuyer[] (the config
// GET returns the whole LeadSaleConfigResponse as-is, matching getLeadSalesConfig).
interface LeadSaleConfigEnvelope {
  config: LeadSaleConfig;
}
interface ListLeadSalesEnvelope {
  count: number;
  entries: LeadSaleRow[];
}
interface SeenBuyersEnvelope {
  count: number;
  buyers: SeenBuyer[];
}

// ---------------------------------------------------------------------------
// Witzzy (Witzzy-first sequential-fallback) types — the admin surface for
// apps/rtb-api/src/witzzy_dispatch.ts (PRP witzzy_sequential). Shapes mirror
// the admin-api witzzy routes (routes/witzzy.ts), which is itself an
// intentional COPY of rtb-api's config/witzzy_config.ts (admin-api cannot
// import rtb-api) — these types MUST stay in lockstep with both. The stage
// ships dark (enabled:false) with an empty destination ARN; this surface only
// READS its witzzy.* logs (+ the Witzzy outcome counts folded into the
// EXISTING LeadSaleStats.outcomes above) and WRITES its operator config
// singleton. `enabled` is owned by the master toggle (the SEND kill switch
// only — pending fallbacks resolve via the separate lead_sale_config.enabled),
// never a config Save.
// ---------------------------------------------------------------------------

/** The route tiers Witzzy can cover — mirrors routes/witzzy.ts's Tier. NOTE:
 *  unlike LeadSaleRoutingTier, there is no "any" (a plain membership filter,
 *  not a routing target). */
export type WitzzyTier =
  | "below_10k"
  | "over_10k"
  | "over_20k"
  | "over_30k"
  | "over_40k";

// ---------------------------------------------------------------------------
// Send window + daily quota (witzzy_send_window_quota) — the two pass-level,
// transient guards on the send pass. Mirrors
// apps/rtb-api/src/domain/send_window.ts + config/witzzy_config.ts and the
// 🔒 lockstep copy in apps/admin-api/src/routes/witzzy.ts (WitzzyConfig +
// witzzyStats) — all three MUST stay in lockstep.
// ---------------------------------------------------------------------------

/** Monday-first weekday key. */
export type WitzzyDayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
/** [open, close) in LOCAL minutes since midnight (open inclusive, close
 *  exclusive), or null = closed all day. */
export type WitzzyDayWindow = [number, number] | null;
/** Per-weekday operating hours, evaluated DST-correctly server-side in `tz`. */
export interface WitzzySendWindow {
  tz: string;
  days: Record<WitzzyDayKey, WitzzyDayWindow>;
}

/** Today's send-window/quota state, off GET …/witzzy/stats. ALWAYS
 *  today-scoped in the configured zone, independent of the stats range
 *  picker (window/start/end). */
export interface WitzzyQuotaStatus {
  day: string;
  tz: string;
  sent_today: number;
  daily_quota: number | null;
  window_open: boolean;
  window_today: [number, number] | null;
}

/** The Witzzy send destination. `webhook_url_secret_arn` is a Secrets Manager
 *  ARN — the URL VALUE is never stored/returned here, only a masked
 *  `webhook_url_hint` (origin + "/…" + last 6 chars). The operator pastes the
 *  raw URL into `webhook_url_new` (write-only, sent on PUT only when
 *  non-empty); admin-api writes it to Secrets Manager and returns only the
 *  ARN + the recomputed hint. Mirrors routes/witzzy.ts:WitzzyDestination.
 *
 *  ASYMMETRIC read/write, unlike lead_sales.ts's SellDestination: a GET always
 *  returns `webhook_url_secret_arn` (+ `webhook_url_hint` once a URL is set),
 *  but the PUT body's destination is `.strict()`-validated server-side to
 *  EXACTLY `{name, webhook_url_new?}` — the ARN is server-authoritative and
 *  round-tripping it would 400 (an unrecognized field). `webhook_url_secret_arn`
 *  is therefore optional here so a PUT body can omit it entirely. */
export interface WitzzyDestination {
  name: string;
  webhook_url_secret_arn?: string;
  webhook_url_hint?: string;
  /** Write-only: the raw URL the operator pastes. Sent on PUT only when a
   *  new/replacement URL is being set; NEVER returned by GET, NEVER stored. */
  webhook_url_new?: string;
  /** TrackDrive lead-post token — the same ARN/masked-hint/write-only-paste
   *  pattern as the webhook URL above, but an independent secret
   *  (`loop-rtb-<env>-witzzy-td-lead-token`). */
  td_lead_token_secret_arn?: string;
  td_lead_token_hint?: string;
  /** Write-only: the raw TrackDrive lead token the operator pastes. Sent on
   *  PUT only when a new/replacement token is being set; NEVER returned by
   *  GET, NEVER stored. */
  td_lead_token_new?: string;
  /** Call-events webhook (witzzy_call_events) — the same ARN/masked-hint/
   *  write-only-paste pattern as the webhook URL above, but an independent
   *  secret (`loop-rtb-<env>-witzzy-events-url`). Used by the witzzy_events
   *  stream consumer to POST call_received_target / call_received_other
   *  events; unrelated to the intake `webhook_url_*` above. */
  events_url_secret_arn?: string;
  events_url_hint?: string;
  /** Write-only: the raw events webhook URL the operator pastes. Sent on PUT
   *  only when a new/replacement URL is being set; NEVER returned by GET,
   *  NEVER stored. */
  events_url_new?: string;
}

/** The singleton Witzzy config (mirrors routes/witzzy.ts:WitzzyConfig).
 *  `wait_ms`/`max_age_ms` are the send-pass eligibility BAND (PUT enforces
 *  `max_age_ms > wait_ms`, 400 invalid_band — same as Lead Sales).
 *  `conversion_window_ms` (X) / `fallback_max_age_ms` are a PAIR: the PUT
 *  enforces `fallback_max_age_ms > conversion_window_ms + 5 min` (the sync
 *  grace), also 400 invalid_band. `enabled` is owned by the master toggle.
 *  `send_window`/`daily_quota` (witzzy_send_window_quota) are two separate
 *  pass-level/transient guards — NOT the conversion/fallback pair above. */
export interface WitzzyConfig {
  enabled: boolean;
  /** Master switch for the TrackDrive lead-post pre-flight that runs before
   *  each Witzzy send (independent of `enabled`). Default false. */
  td_lead_post_enabled: boolean;
  wait_ms: number;
  max_age_ms: number;
  sends_per_min: number;
  conversion_window_ms: number;
  fallback_max_age_ms: number;
  tiers: WitzzyTier[];
  destination: WitzzyDestination;
  success_number_10: string;
  success_traffic_source_id: number | null;
  /** Operating hours — the send pass runs only when open (pass-level,
   *  transient). */
  send_window: WitzzySendWindow;
  /** Max leads handed to Witzzy per LOCAL day (accepted + unconfirmed).
   *  null = unlimited (the default). */
  daily_quota: number | null;
  /** witzzy_call_events — master switch for the witzzy_events stream
   *  consumer (independent of `enabled`/`td_lead_post_enabled`): posts a
   *  call-received event to Witzzy when a Witzzy-sent consumer calls inside
   *  the attribution window. Default false. */
  events_enabled: boolean;
  /** How many days after the send a call is still attributed to Witzzy (the
   *  event window). Whole integer, 1-30; server default 7. */
  attribution_window_days: number;
}

/** GET /api/lead-sales/witzzy/config → the merged config + the SEND per-tick
 *  cap (sends_per_min may rise to it, never past it). Mirrors
 *  getWitzzyConfig's jsonResponse body — returned whole. */
export interface WitzzyConfigResponse {
  config: WitzzyConfig;
  tick_cap: number;
}

/** GET /api/lead-sales/witzzy/stats → windowed sends / outcomes / skips off
 *  the witzzy.* Logs Insights group (unique leads, count_distinct(phone_hash)
 *  per bucket). Mirrors witzzyStats's jsonResponse body. The ledger-backed
 *  Witzzy OUTCOME counts (pending/converted/expired/send_failed) live on the
 *  EXISTING LeadSaleStats.outcomes instead — this is the send/skip funnel
 *  that only the dispatch logs carry. */
export interface WitzzyStats {
  window: { startMs: number; endMs: number };
  sends: Record<string, number>;
  outcomes: Record<string, number>;
  skips: Record<string, number>;
  /** TrackDrive lead-post outcome counts off the witzzy.td_post logs,
   *  count_distinct(phone_hash) by TdLeadOutcome (posted | existing_lead |
   *  rejected_dnc | rejected_dupe | rejected_other | failed |
   *  invalid_phone). OPTIONAL: absent on a response from before this slice
   *  shipped — the tiles render 0 either way, same tolerance as `quota`. */
  td_post?: Record<string, number>;
  /** Today's send-window/quota state (witzzy_send_window_quota) — ALWAYS
   *  today-scoped in the configured zone, independent of `window`/start/end.
   *  OPTIONAL: absent when the server-side read failed, or on a response
   *  from before this slice shipped — the page renders "—" either way. */
  quota?: WitzzyQuotaStatus;
  /** witzzy_call_events — per-call-event counts off the witzzy.event /
   *  witzzy.events_batch Logs Insights group: `sent` by event type
   *  (call_received_target | call_received_other), `status` by per-record
   *  outcome (sent | failed | failed_4xx | release_failed | skipped_window |
   *  skipped_dedupe | watch_lost). OPTIONAL: absent on a response from before
   *  this slice shipped, or when the read failed — the tiles render 0
   *  either way, same tolerance as `td_post`/`quota`.
   *
   *  `converted` (witzzy_converted_7day) — unique consumers whose call to
   *  the Witzzy number within 7 days of the send produced a target event
   *  (count_distinct(phone_hash) via witzzyConvertedQuery). OPTIONAL: an
   *  older server omits it — the "Converted" tile renders 0 either way. */
  events?: {
    sent: Record<string, number>;
    status: Record<string, number>;
    converted?: number;
  };
}

interface WitzzyConfigEnvelope {
  config: WitzzyConfig;
}

// ---------------------------------------------------------------------------
// HTM (Pipes.ai) — the admin surface for the aged-lead resale stage
// (apps/rtb-api/src/htm_dispatch.ts, PRP stages/02_prp/output/
// htm_aged_leads_prp.md Task 7): 72 h after each over-$10K form-fill the tick
// PINGS HTM and — only on success:true — POSTS the lead, recording every
// decision in its OWN `htm-sales` ledger. Shapes mirror the admin-api routes
// (apps/admin-api/src/routes/htm.ts), which is itself a 🔒 LOCKSTEP COPY of
// apps/rtb-api/src/config/htm_config.ts. All three MUST agree field-for-field
// and default-for-default (PRP AC10) — admin-web cannot import either app, so
// the contract is mirrored here as types + the constants below.
// ---------------------------------------------------------------------------

/** Config-table partition key for the singleton (htm_config.ts:HTM_CONFIG_KEY). */
export const HTM_CONFIG_KEY = "htm_config";

/** 🔴 THE FLOOR — 72 h (259_200_000 ms). HTM never receives a lead younger than
 *  this; the reader reverts a lower stored value and the admin PUT 400s it
 *  (`invalid_age`). The page's client guard reads the GET's `min_age_ms` and
 *  falls back to this constant only before the config has loaded. */
export const HTM_MIN_AGE_MS = 72 * 60 * 60 * 1000;

/** `catchup_ms` bounds — 5 min .. 6 h (the server 400s `invalid_catchup`). */
export const HTM_CATCHUP_MIN_MS = 5 * 60 * 1000; // 300_000
export const HTM_CATCHUP_MAX_MS = 6 * 60 * 60 * 1000; // 21_600_000

/** Upper bound for `sends_per_tick` (== the dispatcher's HTM_TICK_CAP; the GET
 *  returns it as `tick_cap`). */
export const HTM_SENDS_PER_TICK_MAX = 100;

/** The four route tiers HTM may cover. `below_10k` is NOT one of them (PRP D8)
 *  — not at the type level, not in the reader, not in the PUT (400
 *  `invalid_tiers`), and the page offers no checkbox for it. */
export type HtmTier = "over_10k" | "over_20k" | "over_30k" | "over_40k";
export const HTM_TIERS: readonly HtmTier[] = [
  "over_10k",
  "over_20k",
  "over_30k",
  "over_40k",
];

/** Which identity the ping carries: md5 phone/email ("hashed", default) or the
 *  raw values ("raw") — operator-switchable without a deploy (PRP D4). */
export type HtmPingFields = "hashed" | "raw";

/** The singleton HTM config (mirrors routes/htm.ts:HtmConfig). `enabled`,
 *  `test_mode` and an empty `api_key_secret_arn` are the three "ships dark"
 *  locks. `api_key_secret_arn` / `api_key_hint` are SERVER-authoritative — set
 *  only by the write-only paste path; this page shows the hint and never the
 *  ARN, and never sends either back. */
export interface HtmConfig {
  enabled: boolean;
  test_mode: boolean;
  age_ms: number;
  catchup_ms: number;
  sends_per_tick: number;
  tiers: HtmTier[];
  ping_fields: HtmPingFields;
  ping_url: string;
  post_url: string;
  api_key_secret_arn: string;
  /** Masked first4…last4 display hint — never the key. */
  api_key_hint?: string;
}

/** Per-field defaults — value-for-value the same as htm_config.ts /
 *  routes/htm.ts DEFAULT_HTM_CONFIG (PRP AC10). Reference only: the page always
 *  renders the server's merged config, never these. */
export const DEFAULT_HTM_CONFIG: HtmConfig = {
  enabled: false,
  test_mode: true,
  age_ms: HTM_MIN_AGE_MS, // 259_200_000
  catchup_ms: 2 * 60 * 60 * 1000, // 7_200_000
  sends_per_tick: 50,
  tiers: ["over_10k", "over_20k", "over_30k", "over_40k"],
  ping_fields: "hashed",
  ping_url: "https://s6dpef2k31.execute-api.us-west-2.amazonaws.com/prodLeadPingApi",
  post_url: "https://leads.pipes.ai/api/lead",
  api_key_secret_arn: "",
};

/** GET /api/lead-sales/htm/config → the merged config + `tick_cap` (max
 *  sends_per_tick) + `min_age_ms` (the 72 h floor, so the client guard and the
 *  server agree) + `age_defaulted` (a stored age under the floor was reverted —
 *  the dispatcher is sending NOTHING, `invalid_age`, until a valid Save). */
export interface HtmConfigResponse {
  config: HtmConfig;
  tick_cap: number;
  min_age_ms: number;
  age_defaulted: boolean;
}

/** PUT /api/lead-sales/htm/config body — the operator-editable knobs ONLY.
 *  `enabled` / `test_mode` are NOT here (only PUT …/flags flips them, behind the
 *  page's confirm gates); the ARN + hint are NOT here (server-authoritative).
 *  🔴 `api_key_new` is WRITE-ONLY and present ONLY when the operator pasted a
 *  non-blank key — the server writes it to Secrets Manager and returns only the
 *  ARN + masked hint. */
export interface HtmConfigUpdate {
  age_ms: number;
  catchup_ms: number;
  sends_per_tick: number;
  tiers: HtmTier[];
  ping_fields: HtmPingFields;
  ping_url: string;
  post_url: string;
  api_key_new?: string;
}

/** PUT /api/lead-sales/htm/flags body — at least one of the two. */
export interface HtmFlagsUpdate {
  enabled?: boolean;
  test_mode?: boolean;
}

/** Every htm-sales ledger status (rtb-api data/htm_sales.ts HtmSaleStatus —
 *  🔒 lockstep with routes/htm.ts:HTM_LEDGER_STATUSES). */
export type HtmLedgerStatus =
  | "in_flight"
  | "posted"
  | "post_rejected"
  | "ping_rejected"
  | "blocked_exclusive"
  | "blocked_witzzy"
  | "missing_fields"
  | "failed";

export type HtmOutcomeCounts = Record<HtmLedgerStatus, number>;

/** GET /api/lead-sales/htm/stats (mirrors routes/htm.ts:HtmStats). `window` is
 *  epoch-MS (the request's start/end are epoch-SECONDS). `outcomes` are the
 *  ledger rows in the window split by each row's OWN `test_mode` (live vs test
 *  — PRP F6: test counts are never read as revenue); `rejected_by_reason` groups
 *  HTM's reason text per stage. `considered` / `skips_by_reason` / `pinged` /
 *  the tick counters come off the htm-dispatch Logs Insights group
 *  (best-effort → zeros). `test_mode_now` = the stored flag, for the badge. */
export interface HtmStats {
  window: { start: number; end: number };
  test_mode_now: boolean;
  outcomes: { live: HtmOutcomeCounts; test: HtmOutcomeCounts };
  rejected_by_reason: { ping: Record<string, number>; post: Record<string, number> };
  considered: number;
  skips_by_reason: Record<string, number>;
  pinged: number;
  deadline_hit_ticks: number;
  band_saturated_ticks: number;
}

interface HtmConfigEnvelope {
  config: HtmConfig;
}

// ---------------------------------------------------------------------------
// Outbound calling — the "posted → called → revenue" report, by Pacific
// posting day (outbound_calling PRP v2). READ-ONLY: two GET endpoints off
// the lead-sales ledger joined to td-calls; no write surface. Shapes mirror
// admin-api routes/outbound_calling.ts. `destination` is always "witzzy"
// today — the param exists so LeadExec can be added without an API break.
// ---------------------------------------------------------------------------

/** One Pacific posting-day cohort row off GET
 *  /api/lead-sales/outbound/cohorts. `posted` = candidates found for the
 *  day; `examined` = those actually joined to td-calls (may be < posted
 *  when the per-day lead cap was hit); `complete` = examined === posted — a
 *  capped day is NEVER rendered as a silent zero, it renders `complete:
 *  false` instead. `called_target`/`called_elsewhere` are INDEPENDENT (a
 *  consumer can be both); `called_elsewhere_only` is the exclusive subset.
 *  `offsets[i]` = consumers whose FIRST target call landed on day-offset i
 *  (0..window_days-1); invariant: sum(offsets) === called_target.
 *  `median_minutes_to_call`/`p90_minutes_to_call` are null (never NaN/0)
 *  when called_target === 0. */
export interface OutboundCohortRow {
  day: string; // "YYYY-MM-DD", Pacific posting day (send day, not sold_day)
  posted: number;
  examined: number;
  complete: boolean;
  called_target: number;
  called_elsewhere: number;
  called_elsewhere_only: number;
  offsets: number[]; // length window_days (7)
  connected: number;
  paid: number;
  revenue_cents: number;
  median_minutes_to_call: number | null;
  p90_minutes_to_call: number | null;
}

/** GET /api/lead-sales/outbound/cohorts?days=&end=&destination= → the cohort
 *  table + totals for the requested window. `totals` sums the integer
 *  fields across the returned days and RECOMPUTES the medians over the
 *  pooled per-lead minutes-to-call (percentiles don't sum) — never derive
 *  totals client-side from the per-day rows. `partial` = something failed
 *  and was swallowed (a per-lead call Query, a ledger page, the config
 *  read); `partial_reasons` is a bounded vocabulary
 *  (`lead_calls_query_failed` | `ledger_page_failed` | `config_read_failed`).
 *  `truncated` = a per-day cap or the request deadline fired (the count is
 *  short). */
export interface OutboundCohortsResponse {
  window: { start_ms: number; end_ms: number; tz: string; days: number };
  destination: string;
  window_days: number; // the FIXED attribution window (7) — not the `days` param
  cohorts: OutboundCohortRow[];
  // Same fields as OutboundCohortRow minus `day` (a total has none) and
  // `offsets` (not meaningful pooled — AC5's per-row invariant covers that).
  totals: Omit<OutboundCohortRow, "day" | "offsets">;
  partial: boolean;
  partial_reasons: string[];
  truncated: boolean;
  leads_examined: number;
}

/** One lead row off GET /api/lead-sales/outbound/leads (the drill-down for a
 *  single Pacific posting day, newest send first). `phone_masked` is the
 *  ONLY phone field — no raw phone is ever returned. `event_sent` is a TICK
 *  only (whether the ledger's witzzy_event_call_ids set already reflects the
 *  first target call) — never the source of truth for counting. Optional
 *  fields are absent, never null, when the lead has no such data (e.g. a
 *  lead with no target call has no `first_target_at_ms`/`minutes_to_call`/
 *  `day_offset`). */
export interface OutboundLeadRow {
  phone_masked: string;
  client_id?: string;
  tier?: string;
  sent_at_ms: number;
  status: string; // TrackDrive's free-form lead status — display only, never logic
  witzzy_outcome?: string;
  first_target_at_ms?: number;
  minutes_to_call?: number;
  day_offset?: number; // clamped 0..window_days-1
  target_calls: number;
  other_calls: number;
  connected: boolean;
  paid: boolean;
  revenue_cents: number;
  buyer_name?: string;
  event_sent: boolean;
}

/** GET /api/lead-sales/outbound/leads?day=&destination=&limit=&offset= → one
 *  day's leads, newest send first. Pagination is a PLAIN INTEGER offset
 *  (never a cursor carrying a phone) — advance by passing the previous
 *  page's `next_offset` back as the next call's `offset`; `next_offset` is
 *  null on the last page. */
export interface OutboundLeadsResponse {
  day: string;
  leads: OutboundLeadRow[];
  total: number;
  next_offset: number | null;
  partial: boolean;
  partial_reasons: string[];
  truncated: boolean;
}

// ---------------------------------------------------------------------------
// "Yes" conversational-iMessage types — the yes-engine admin surface (config
// incl. the campaigns array + keyword lists + on/off + stats + recent
// conversations). Shapes mirror the admin-api yes routes (routes/yes.ts). The
// YesConfig contract is an intentional COPY of rtb-api's config/yes_config.ts
// (admin-api cannot import rtb-api), so these MUST stay in lockstep with the
// server. The engine ships dark (enabled:false) with no campaigns — this surface
// only READS its yes.* logs + the yes-conversations ledger and WRITES its
// operator config singleton. `enabled` is owned by the master toggle, never a
// config Save.
// ---------------------------------------------------------------------------

/** A per-threshold "yes" campaign (mirrors yes.ts:YesCampaign). `id` is a stable
 *  opaque id the ledger's campaign_id + the by-campaign stats key on.
 *
 *  🔴 v4 — THE AFFIRMATIVE REPLY SENDS **TWO** MESSAGES:
 *    message 1 = `intro_copy`      (the first contact)
 *    message 2 = `followup_copy`   (the response to a "yes")
 *    message 3 = `callback_number` ALONE, immediately after message 2
 *  Message 3 exists because a number buried in a sentence is NOT tappable in
 *  iMessage; alone in its own message it auto-links. Both copy fields still support
 *  the merge tags — `{n}` (/`{first_name}`) = the lead's first name, `{p}` =
 *  `callback_number` — but `followup_copy` NO LONGER NEEDS to carry the number. */
export interface YesCampaign {
  id: string;
  name: string;
  enabled: boolean;
  min_debt_threshold: number;
  intro_copy: string;
  followup_copy: string;
  /** The callback number. 🔴 v4: it is SENT AS ITS OWN MESSAGE (message 3) right after
   *  the follow-up, VERBATIM — exactly as typed here, never reformatted — so an operator
   *  should NOT also inline it in `followup_copy` (that would send it twice). It is ALSO
   *  still what the `{p}` merge tag renders, for copy that wants it inline as well.
   *  Absent ⇒ `{p}` renders empty AND there is no message 3 (the reply is then recorded
   *  `followup_failed` / `number_send_failed`, since the lead got no number).
   *  Omit the key entirely when blank — never send `""`.
   *
   *  🔴 v3: this is ALSO THE CALL-ATTRIBUTION KEY. The engine snapshots it onto each
   *  conversation at intro time and `YesStats.calls` counts a call only when the number
   *  the consumer DIALLED matches that snapshot — so editing it changes which calls
   *  attribute to this campaign going forward (already-messaged leads keep theirs). */
  callback_number?: string;
  /** v5 OPTIONAL: timed nudges sent after the intro while the lead has not
   *  replied (each `delay_ms` measured from the previous message). Max 5; a
   *  reply cancels any pending step; the engine never sends a step more than 30
   *  minutes past due. Rendered + snapshotted per conversation at intro time. */
  pre_reply_steps?: YesStep[];
  /** v5 OPTIONAL: delay (ms) between the affirmative follow-up and the bare
   *  number message. OMIT for v4's immediate send (never send 0); when present
   *  the number goes out via the dispatcher tick (±1 min granularity). */
  number_delay_ms?: number;
}

/** v5: one timed pre-reply step (mirrors yes.ts:YesStep). */
export interface YesStep {
  copy: string;
  delay_ms: number;
}

/** v5: the LLM reply-handling ladder (mirrors yes.ts:YES_LLM_MODES). "draft" is
 *  deliberately NOT a member of this build; the engine merges any unknown stored
 *  value to "off" (fail dark). */
export type YesLlmMode = "off" | "shadow" | "classify";

/** The singleton "yes" engine config (mirrors yes.ts:YesConfig). `wait_ms`/
 *  `max_age_ms` are a PAIR defining a narrow eligibility BAND; the PUT enforces
 *  `max_age_ms > wait_ms` (400 invalid_band) — the reader does not.
 *
 *  🔴 `from_number` / `priority_id` / `channel_type` are MUTUALLY EXCLUSIVE Bloo
 *  routing OVERRIDES, all UNSET BY DEFAULT: leaving all three blank is the
 *  recommended setting — the engine then sends AGNOSTICALLY and Bloo rotates its
 *  own numbers, keeps a lead on the number it already has history with, skips
 *  banned numbers and enforces the per-number daily cap. Setting from_number AND
 *  priority_id together is a 400 invalid_routing. `enabled` is owned by the master
 *  toggle, never a config Save. */
export interface YesConfig {
  enabled: boolean;
  wait_ms: number;
  max_age_ms: number;
  msgs_per_min: number;
  from_number?: string;
  priority_id?: string;
  channel_type?: string;
  affirmative_keywords: string[];
  stop_keywords: string[];
  conversation_ttl_ms: number;
  campaigns: YesCampaign[];
  /** 🔴 v3, and the ONLY knob v3 adds: post the lead to TrackDrive (Arkade's own
   *  account) just before the intro. Default TRUE — TrackDrive must know the lead before
   *  the consumer can call the advertised number back. BEST-EFFORT: a TrackDrive failure
   *  or rejection never blocks, defers or fails the message. REQUIRED here because the
   *  UI always posts the whole config object (the server's schema is `.strict()`).
   *
   *  🔴 There is NO DNC knob, deliberately: this is iMessage, not dialling, so a
   *  `rejected_dnc` outcome is a LABEL ONLY and the intro still sends. */
  trackdrive_post_enabled: boolean;
  /** 🔴 v5: the LLM reply-handling mode. REQUIRED on Save (the server's schema
   *  is strict about omission for the same reason as `trackdrive_post_enabled`:
   *  an absent knob is how a stored mode silently resets). Unlike `enabled`,
   *  a config Save DOES set it — it is a mode, not the master kill switch.
   *    off      — v4 behaviour, no LLM anywhere (the default).
   *    shadow   — verdicts fetched + recorded on keyword-"other" replies;
   *               the engine still ACTS on keywords. Pure measurement.
   *    classify — an `engaged` verdict triggers the follow-up + number flow;
   *               every other verdict and every LLM failure behaves as v4. */
  llm_mode: YesLlmMode;
}

/** GET /api/yes/config → the merged config + the intro per-tick cap (msgs_per_min
 *  may rise to it, never past it). Mirrors getYesConfig's jsonResponse body —
 *  returned whole (like dialerConfig / leadSalesConfig). */
export interface YesConfigResponse {
  config: YesConfig;
  tick_cap: number;
}

/** The per-conversation-state outcome counts (mirrors yes.ts:OutcomeCounts /
 *  YES_STATE_KEYS — ALL TEN members of the ledger's state union). Tallied from the
 *  yes-conversations ledger, so a conversation moves between these buckets over its
 *  life (intro_sent → converted / opted_out / replied_other as the inbound webhook
 *  applies the reply). */
export interface YesOutcomeCounts {
  intro_sending: number;
  intro_sent: number;
  converted: number;
  opted_out: number;
  replied_other: number;
  undeliverable: number;
  send_failed: number;
  /** 🔴 NOT a failure: the send was refused for a TRANSIENT reason (capacity, an
   *  upstream blip), so the once-per-lead claim was RELEASED and the lead WILL be
   *  retried while it is still inside the eligibility band. Must never be presented
   *  in the failure bucket. */
  send_deferred: number;
  followup_failed: number;
  /** 🔴 v5 — the 11th state: replied yes, follow-up sent, the bare number is
   *  SCHEDULED (dispatcher tick) but not yet sent. Normally transits within ~a
   *  minute; a large standing count means the step pass is not running. */
  number_pending: number;
  /** A TTL-sweep outcome nothing writes today; surfaced so the buckets sum to the
   *  ledger's row count and no state can be silently swallowed. */
  expired: number;
}

/** How the best-effort TrackDrive lead POST went, counted over the window's ledger
 *  rows (mirrors yes.ts:TD_POST_KEYS / emptyTdPost). This is the observability that
 *  makes a best-effort side-effect SAFE: a 100% rejection rate looks perfectly healthy
 *  from the messaging side and is visible ONLY here.
 *
 *  🔴 `rejected_dnc` is INFORMATIONAL. Those leads WERE messaged — no suppression
 *  exists anywhere in this engine (iMessage is not dialling), so the row must never be
 *  read as "withheld". */
export interface YesTdPostCounts {
  /** 201 — TrackDrive created the lead. */
  posted: number;
  /** 200 — TrackDrive already had this lead (e.g. the dialer posted it first). Also a
   *  success: TrackDrive knows the lead, which is the whole point of the post. */
  existing_lead: number;
  rejected_dupe: number;
  /** 🔴 A LABEL ONLY — the intro was still sent (see the interface note). */
  rejected_dnc: number;
  rejected_other: number;
  /** Transport / auth / timeout. */
  failed: number;
  invalid_phone: number;
  /** A bookkeeping error (the durable record is the dispatcher's log line). */
  post_error: number;
  /** No outcome recorded on the row at all: the post knob was OFF (the dispatcher then
   *  writes nothing), the row predates v3, or the label is unrecognised. 🔴 Bucketed,
   *  never dropped, so the tally reconciles with the funnel's row count. */
  not_posted: number;
}

/** GET /api/yes/stats → the outcome funnel (by conversation state, from the
 *  ledger) + the same breakdown per campaign, per tier and per SENDER number, plus
 *  the pre-claim SKIP breakdown (from CloudWatch Logs Insights over yes.skip),
 *  windowed. All counts default to 0 server-side. Mirrors yesStats's jsonResponse
 *  body. */
export interface YesStats {
  window: { startMs: number; endMs: number };
  outcomes: YesOutcomeCounts;
  /** `calls` = the DID-attributed iMessage calls for that campaign (see `calls` below). */
  by_campaign: Array<{
    campaign_id: string;
    outcomes: YesOutcomeCounts;
    calls: number;
  }>;
  by_tier: Array<{ tier: string; outcomes: YesOutcomeCounts }>;
  /** Send observability: the same outcome counts grouped by the sender number Bloo
   *  RESOLVED for each row. Read-only — Bloo owns rotation and the per-number daily
   *  caps, so there are no capacity figures here and nothing to enforce.
   *  `sender_number` is OUR number (not consumer PII) and is UNMASKED; rows written
   *  before the sender was recorded — or whose send never reached Bloo — group under
   *  `"unknown"`. */
  by_sender: Array<{
    sender_number: string;
    outcomes: YesOutcomeCounts;
    /** 🔴 REAL Bloo delivery receipts for that number — the EARLY-WARNING signal: a
     *  number whose accepted count holds flat while this falls is degrading. */
    delivered: number;
  }>;
  skips: {
    invalid_phone: number;
    quiet_hours: number;
    no_debt: number;
    no_campaign: number;
    exclusive_check_failed: number;
    blocked_exclusive: number;
    already_contacted: number;
    lead_error: number;
    /** Tick-level, not per-lead: the dispatcher's consecutive-send-failure breaker
     *  tripped and ended the tick early (an upstream outage). */
    consecutive_send_failures: number;
  };
  /** v3: the best-effort TrackDrive lead-POST outcome tally over the window. */
  td_post: YesTdPostCounts;
  /** v3: conversations with a REAL Bloo delivery receipt. 🔴 A DIFFERENT FACT from
   *  `outcomes.intro_sent`, which only means Bloo ACCEPTED the send — which is why the
   *  page's old wording for that card (it claimed delivery) was wrong. Delivery is a
   *  receipt on the row, NOT a conversation state (the union stays at ten members). */
  delivered: number;
  /** v3: `delivered` ÷ the conversations whose send Bloo ACCEPTED (the messaged states,
   *  which EXCLUDE send_failed / send_deferred / intro_sending — no message exists for
   *  those). 🔴 0 when the denominator is 0, so render "—" rather than "0%" then. */
  delivery_rate: number;
  /** v3: inbound calls attributed to an iMessage intro.
   *
   *  🔴 Read from the **NEW** TrackDrive account (arkade-ai-inc), READ-ONLY, and it
   *  GATES NOTHING — no send, claim or config decision depends on it. A call counts only
   *  when the number the consumer DIALLED is the DID we advertised to that lead, the
   *  caller is that lead's own number, and the call is at-or-after its intro. That DID
   *  match is what keeps the DIALER's dial-backs (same account, same caller) out.
   *
   *  `imsg_tagged` is CORROBORATION ONLY, never a filter: a lead the dialer had already
   *  posted keeps its original traffic source, so `attributed > imsg_tagged` is normal.
   *
   *  🔴 `status` matters: "partial" = the pagination bound was hit (the figure is
   *  BOUNDED), "unavailable" = the read failed or no credential is set (the figure is
   *  MISSING — never present it as a confirmed zero). */
  calls: {
    attributed: number;
    imsg_tagged: number;
    status: "ok" | "partial" | "unavailable";
  };
}

/** One recent-conversation row off the yes-conversations by-day GSI (mirrors
 *  yes.ts:ConversationEntry + mapConversationRow). Phone is masked server-side;
 *  campaign_id / tier / state are coerced to "" (NOT null); the reply text +
 *  timestamps + undeliverable_reason + client_id are nullable (reply_text /
 *  undeliverable_reason are length-bounded server-side as they echo raw text). */
export interface YesConversationRow {
  phone_masked: string;
  campaign_id: string;
  tier: string;
  state: string;
  contacted_at_ms: number;
  reply_text: string | null;
  replied_at_ms: number | null;
  followup_sent_at_ms: number | null;
  undeliverable_reason: string | null;
  client_id: string | null;
  /** v3: this lead's TrackDrive lead-POST outcome (our own bounded label), or null when
   *  none was recorded (the post knob was off, or the row predates v3). */
  td_lead_outcome: string | null;
  /** v3: when Bloo's delivery receipt arrived for this conversation, or null. */
  delivered_at_ms: number | null;
  /** 🔴 v4: Bloo's message id for MESSAGE 3 — the callback number sent ALONE right after
   *  the follow-up. `null` when message 3 never landed (or a pre-v4 row). */
  number_msg_id: string | null;
  /** 🔴 v4: when MESSAGE 3 (the bare number) was accepted, or null. Paired with the
   *  state: a `followup_failed` row with `undeliverable_reason: "number_send_failed"` is a
   *  PARTIAL handoff — the lead got the response but NOT the number. */
  number_sent_at_ms: number | null;
  /** 🔴 v5: the LLM's bounded verdict for the reply (engaged | not_interested |
   *  question | unclear | llm_error), or null when the LLM was never consulted.
   *  THE SHADOW-REVIEW SURFACE: classify is promoted only after David reviews
   *  these against real "other" replies (decision #4). Our own vocabulary,
   *  never model text. */
  llm_verdict: string | null;
}

// The config PUT + enabled routes wrap the saved config in a { config } envelope;
// the conversations route wraps the list in { count, entries }. Both are unwrapped
// at the boundary below so callers get the bare YesConfig / YesConversationRow[]
// (the config GET returns the whole YesConfigResponse as-is, matching getYesConfig).
interface YesConfigEnvelope {
  config: YesConfig;
}
interface ListYesConversationsEnvelope {
  count: number;
  entries: YesConversationRow[];
}

// ---------------------------------------------------------------------------
// User Management + Identity types — the /api/me identity probe and the
// admin-only /api/users management surface. Shapes mirror the admin-api routes
// (routes/me.ts + routes/users.ts) and lib/authz.ts SECTIONS.
// ---------------------------------------------------------------------------

/** The nine dashboard sections a user can be granted (mirrors admin-api SECTIONS). */
export type Section =
  | "debt-lookup"
  | "inbound-rtb"
  | "legal-widget"
  | "sms"
  | "logs"
  | "reporting"
  | "dialer"
  | "lead-sales"
  | "yes";

/**
 * GET /api/me — the caller's own identity + live section grants. `email`/`sub`
 * are null when the Cognito claim is absent (mirrors admin-api routes/me.ts).
 */
export interface MeResponse {
  email: string | null;
  sub: string | null;
  is_admin: boolean;
  sections: Section[];
}

/**
 * One pool user row (mirrors admin-api routes/users.ts `toRow` / AdminUserRow).
 * `created_at` is an ISO-8601 string, or null when Cognito omits UserCreateDate.
 */
export interface AdminUserRow {
  sub: string;
  email: string;
  enabled: boolean;
  /** Cognito UserStatus (e.g. CONFIRMED, FORCE_CHANGE_PASSWORD). */
  status: string;
  is_admin: boolean;
  sections: Section[];
  created_at: string | null;
}

/** POST /api/users request body. */
export interface CreateUserInput {
  email: string;
  sections: Section[];
  is_admin: boolean;
}

/** POST /api/users response — the new row plus its one-time password. */
export interface CreateUserResponse {
  user: AdminUserRow;
  /** Generated permanent password — shown to the operator exactly once. Never persist. */
  password: string;
}

/** PUT /api/users/{sub}/sections request body — the exact desired grant set. */
export interface PutUserSectionsInput {
  sections: Section[];
  is_admin: boolean;
}

// The list route wraps rows in { users }; put-sections/enable/disable each wrap
// the updated row in { user }. Both are unwrapped at the boundary below so
// callers get the bare AdminUserRow[] / AdminUserRow.
interface ListUsersEnvelope {
  users: AdminUserRow[];
}
interface UserEnvelope {
  user: AdminUserRow;
}

// ---------------------------------------------------------------------------
// Public functions
// ---------------------------------------------------------------------------

// Server response envelopes per PRP §13.5. The SPA unwraps them at the
// API boundary so page code keeps using the flat shapes above.
interface ListClientsEnvelope {
  clients: ClientListItem[];
}
interface GetClientEnvelope {
  client: ClientListItem;
  stats7d: Stats7d;
}
interface PatchClientEnvelope {
  client: ClientListItem;
}
interface ListMirrorClientsEnvelope {
  clients: MirrorClientListItem[];
}
interface GetMirrorClientEnvelope {
  client: MirrorClientListItem;
  stats7d: MirrorStats7d;
}
interface PatchMirrorClientEnvelope {
  client: MirrorClientListItem;
}
interface ListSmsCampaignsEnvelope {
  campaigns: SmsCampaignListItem[];
}
interface GetSmsCampaignEnvelope {
  campaign: SmsCampaignDetailResponse;
}
interface PatchSmsCampaignEnvelope {
  campaign: SmsCampaignListItem;
}
interface CreateSmsCampaignEnvelope {
  campaign: SmsCampaignListItem;
}

export const api = {
  async listClients(): Promise<ClientListItem[]> {
    const r = await apiRequest<ListClientsEnvelope>("/api/clients");
    return r.clients;
  },
  async getClient(id: string): Promise<ClientDetailResponse> {
    const r = await apiRequest<GetClientEnvelope>(
      `/api/clients/${encodeURIComponent(id)}`,
    );
    return { ...r.client, stats7d: r.stats7d };
  },
  createClient(body: ClientCreateRequest): Promise<ClientCreateResponse> {
    return apiRequest<ClientCreateResponse>("/api/clients", {
      method: "POST",
      body,
    });
  },
  async patchClient(
    id: string,
    body: ClientPatchRequest,
  ): Promise<ClientListItem> {
    const r = await apiRequest<PatchClientEnvelope>(
      `/api/clients/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    );
    return r.client;
  },
  rotateKey(id: string): Promise<RotateKeyResponse> {
    return apiRequest<RotateKeyResponse>(
      `/api/clients/${encodeURIComponent(id)}/rotate-key`,
      { method: "POST" },
    );
  },
  dashboard(): Promise<DashboardResponse> {
    return apiRequest<DashboardResponse>("/api/stats/dashboard");
  },
  clientStats(id: string): Promise<ClientStatsResponse> {
    return apiRequest<ClientStatsResponse>(
      `/api/stats/clients/${encodeURIComponent(id)}`,
    );
  },
  clientYield(id: string): Promise<ClientYieldResponse> {
    return apiRequest<ClientYieldResponse>(
      `/api/stats/clients/${encodeURIComponent(id)}/yield`,
    );
  },

  // --- Mirror (Debt Lookup) -------------------------------------------------
  async listMirrorClients(): Promise<MirrorClientListItem[]> {
    const r = await apiRequest<ListMirrorClientsEnvelope>(
      "/api/mirror-clients",
    );
    return r.clients;
  },
  async getMirrorClient(id: string): Promise<MirrorClientDetailResponse> {
    const r = await apiRequest<GetMirrorClientEnvelope>(
      `/api/mirror-clients/${encodeURIComponent(id)}`,
    );
    return { ...r.client, stats7d: r.stats7d };
  },
  createMirrorClient(
    body: MirrorClientCreateRequest,
  ): Promise<MirrorClientCreateResponse> {
    return apiRequest<MirrorClientCreateResponse>("/api/mirror-clients", {
      method: "POST",
      body,
    });
  },
  async patchMirrorClient(
    id: string,
    body: MirrorClientPatchRequest,
  ): Promise<MirrorClientListItem> {
    const r = await apiRequest<PatchMirrorClientEnvelope>(
      `/api/mirror-clients/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    );
    return r.client;
  },
  rotateMirrorKey(id: string): Promise<RotateKeyResponse> {
    return apiRequest<RotateKeyResponse>(
      `/api/mirror-clients/${encodeURIComponent(id)}/rotate-key`,
      { method: "POST" },
    );
  },
  mirrorDashboard(params?: {
    clientId?: string;
    time?: TimeSel;
  }): Promise<MirrorDashboardResponse> {
    const q = new URLSearchParams();
    if (params?.clientId && params.clientId !== "all") {
      q.set("clientId", params.clientId);
    }
    if (params?.time?.kind === "window") {
      q.set("window", String(params.time.windowSec));
    } else if (params?.time?.kind === "range") {
      q.set("start", String(params.time.startSec));
      q.set("end", String(params.time.endSec));
    }
    const qs = q.toString();
    return apiRequest<MirrorDashboardResponse>(
      `/api/stats/mirror${qs ? `?${qs}` : ""}`,
    );
  },
  errorLogs(params: {
    kind: ErrorLogKind;
    windowSec: number;
    limit?: number;
  }): Promise<ErrorLogResponse> {
    const q = new URLSearchParams({
      kind: params.kind,
      window: String(params.windowSec),
      ...(params.limit ? { limit: String(params.limit) } : {}),
    });
    return apiRequest<ErrorLogResponse>(`/api/logs/errors?${q.toString()}`);
  },
  accessDenials(params: {
    windowSec: number;
    limit?: number;
  }): Promise<AccessDenialsResponse> {
    const q = new URLSearchParams({
      window: String(params.windowSec),
      ...(params.limit ? { limit: String(params.limit) } : {}),
    });
    return apiRequest<AccessDenialsResponse>(
      `/api/logs/access-denials?${q.toString()}`,
    );
  },
  leads(params: {
    clientId: string; // "all" or a client_id
    time: TimeSel;
    limit?: number;
  }): Promise<LeadsResponse> {
    const q = new URLSearchParams({ clientId: params.clientId });
    if (params.time.kind === "window") {
      q.set("window", String(params.time.windowSec));
    } else {
      q.set("start", String(params.time.startSec));
      q.set("end", String(params.time.endSec));
    }
    if (params.limit) q.set("limit", String(params.limit));
    return apiRequest<LeadsResponse>(`/api/leads?${q.toString()}`);
  },
  debtMix(params: { clientId: string; time: TimeSel }): Promise<DebtMixResponse> {
    const q = new URLSearchParams({ clientId: params.clientId });
    if (params.time.kind === "window") q.set("window", String(params.time.windowSec));
    else {
      q.set("start", String(params.time.startSec));
      q.set("end", String(params.time.endSec));
    }
    return apiRequest<DebtMixResponse>(`/api/stats/debt-mix?${q.toString()}`);
  },
  getDebtTiers(): Promise<DebtTiersResponse> {
    return apiRequest<DebtTiersResponse>("/api/settings/debt-tiers");
  },
  putDebtTiers(codes: TierCode[]): Promise<DebtTiersResponse> {
    return apiRequest<DebtTiersResponse>("/api/settings/debt-tiers", {
      method: "PUT",
      body: { codes },
    });
  },

  // --- SMS Campaigns + Stats + Settings -------------------------------------
  async listSmsCampaigns(): Promise<SmsCampaignListItem[]> {
    const r = await apiRequest<ListSmsCampaignsEnvelope>("/api/sms/campaigns");
    return r.campaigns;
  },
  async getSmsCampaign(id: string): Promise<SmsCampaignDetailResponse> {
    const r = await apiRequest<GetSmsCampaignEnvelope>(
      `/api/sms/campaigns/${encodeURIComponent(id)}`,
    );
    return r.campaign;
  },
  async createSmsCampaign(
    body: SmsCampaignCreateRequest,
  ): Promise<SmsCampaignListItem> {
    // The server wraps the created campaign in a { campaign } envelope (same as
    // get/patch); unwrap it so callers get a bare SmsCampaignListItem (and a
    // defined campaign_id for the post-create redirect).
    const r = await apiRequest<CreateSmsCampaignEnvelope>("/api/sms/campaigns", {
      method: "POST",
      body,
    });
    return r.campaign;
  },
  async patchSmsCampaign(
    id: string,
    body: SmsCampaignPatchRequest,
  ): Promise<SmsCampaignListItem> {
    const r = await apiRequest<PatchSmsCampaignEnvelope>(
      `/api/sms/campaigns/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    );
    return r.campaign;
  },
  getCampaignRuns(id: string): Promise<SmsCampaignRunsResponse> {
    return apiRequest<SmsCampaignRunsResponse>(
      `/api/sms/campaigns/${encodeURIComponent(id)}/runs`,
    );
  },
  smsStats(params: {
    start: number; // epoch SECONDS (existing TimeSel convention — see debtMix/leads)
    end: number; // epoch SECONDS
    campaignId?: string;
    vertical?: string;
  }): Promise<SmsStatsResponse> {
    const q = new URLSearchParams();
    q.set("start", String(params.start));
    q.set("end", String(params.end));
    if (params.campaignId) q.set("campaignId", params.campaignId);
    if (params.vertical) q.set("vertical", params.vertical);
    return apiRequest<SmsStatsResponse>(`/api/stats/sms?${q.toString()}`);
  },
  getSmsDelay(): Promise<SmsDelaySetting> {
    return apiRequest<SmsDelaySetting>("/api/settings/sms-delay");
  },
  putSmsDelay(sendDelayMs: number): Promise<SmsDelaySetting> {
    return apiRequest<SmsDelaySetting>("/api/settings/sms-delay", {
      method: "PUT",
      body: { sendDelayMs },
    });
  },
  getSmsAutoDispatch(): Promise<SmsAutoDispatchSetting> {
    return apiRequest<SmsAutoDispatchSetting>(
      "/api/settings/sms-auto-dispatch",
    );
  },
  putSmsAutoDispatch(enabled: boolean): Promise<SmsAutoDispatchSetting> {
    return apiRequest<SmsAutoDispatchSetting>("/api/settings/sms-auto-dispatch", {
      method: "PUT",
      body: { enabled },
    });
  },

  // Singleton Auto Send config (MIS-967). The server wraps the config in a
  // { config, source } envelope; we return it as-is so callers get both the
  // bare config object and whether it was stored vs the per-field defaults.
  getAutoSendConfig(): Promise<AutoSendConfigResponse> {
    return apiRequest<AutoSendConfigResponse>("/api/settings/auto-send");
  },
  putAutoSendConfig(config: AutoSendConfig): Promise<AutoSendConfigResponse> {
    return apiRequest<AutoSendConfigResponse>("/api/settings/auto-send", {
      method: "PUT",
      body: config,
    });
  },

  // --- SMS list-upload recipients (MIS-964/965) -----------------------------
  // POST the raw CSV string as the request body. The route accepts raw CSV text
  // OR a {csv:"..."} JSON object; we send raw text with an explicit text/csv
  // content-type (apiRequest forwards a string body verbatim and only defaults
  // the content-type to application/json when one isn't already set).
  uploadSmsRecipients(
    id: string,
    csvText: string,
  ): Promise<SmsRecipientUploadResult> {
    return apiRequest<SmsRecipientUploadResult>(
      `/api/sms/campaigns/${encodeURIComponent(id)}/recipients`,
      {
        method: "POST",
        headers: { "content-type": "text/csv" },
        body: csvText,
      },
    );
  },
  async getSmsRecipientSummary(id: string): Promise<SmsRecipientSummary> {
    const r = await apiRequest<SmsRecipientSummaryEnvelope>(
      `/api/sms/campaigns/${encodeURIComponent(id)}/recipients?summary`,
    );
    return r.counts;
  },

  // --- GRT Conversion Postbacks (Reporting) ---------------------------------
  listGrtPostbacks(params: {
    time: TimeSel;
    campaign?: string; // "all"/absent → no campaign filter
    q?: string;
    limit?: number;
  }): Promise<ListGrtPostbacksEnvelope> {
    const q = new URLSearchParams();
    if (params.time.kind === "window") {
      q.set("window", String(params.time.windowSec));
    } else {
      q.set("start", String(params.time.startSec));
      q.set("end", String(params.time.endSec));
    }
    if (params.campaign && params.campaign !== "all") {
      q.set("campaign", params.campaign);
    }
    if (params.q) q.set("q", params.q);
    if (params.limit) q.set("limit", String(params.limit));
    return apiRequest<ListGrtPostbacksEnvelope>(
      `/api/grt-postbacks?${q.toString()}`,
    );
  },

  // --- Dialer (TrackDrive callback dialer) ----------------------------------
  // config GET returns the whole DialerConfigResponse as-is (config + display
  // extras); the config PUT + enabled routes wrap the saved config in { config }
  // (unwrapped here); dials wraps the list in { count, entries } (unwrapped).
  dialerConfig(): Promise<DialerConfigResponse> {
    return apiRequest<DialerConfigResponse>("/api/dialer/config");
  },
  async putDialerConfig(config: DialConfig): Promise<DialConfig> {
    const r = await apiRequest<DialerConfigEnvelope>("/api/dialer/config", {
      method: "PUT",
      body: config,
    });
    return r.config;
  },
  async setDialerEnabled(enabled: boolean): Promise<DialConfig> {
    const r = await apiRequest<DialerConfigEnvelope>("/api/dialer/enabled", {
      method: "PUT",
      body: { enabled },
    });
    return r.config;
  },
  dialerStats(opts?: {
    windowSec?: number;
    start?: number; // epoch SECONDS (existing TimeSel convention — see smsStats)
    end?: number; // epoch SECONDS
  }): Promise<DialerStats> {
    // Same window/range params as mirrorDashboard, matching the server's
    // readDialWindow precedence: explicit start/end wins, else a rolling
    // window, else the server default (Pacific Today) when nothing is passed.
    const q = new URLSearchParams();
    if (opts?.start !== undefined && opts?.end !== undefined) {
      q.set("start", String(opts.start));
      q.set("end", String(opts.end));
    } else if (opts?.windowSec !== undefined) {
      q.set("window", String(opts.windowSec));
    }
    const qs = q.toString();
    return apiRequest<DialerStats>(`/api/dialer/stats${qs ? `?${qs}` : ""}`);
  },
  async dialerDials(limit?: number): Promise<DialerDialRow[]> {
    const q = new URLSearchParams();
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    const r = await apiRequest<ListDialsEnvelope>(
      `/api/dialer/dials${qs ? `?${qs}` : ""}`,
    );
    return r.entries;
  },

  // --- Lead Sales (the lead-sale proxy) -------------------------------------
  // config GET returns the whole LeadSaleConfigResponse as-is (config +
  // tick_cap); the config PUT + enabled routes wrap the saved config in
  // { config } (unwrapped here); sales wraps the list in { count, entries } and
  // seen-buyers in { count, buyers } (both unwrapped). The config PUT body is
  // the WHOLE LeadSaleConfig incl. `enabled` (the server ignores the posted
  // enabled and preserves the stored value — the master toggle owns it).
  leadSalesConfig(): Promise<LeadSaleConfigResponse> {
    return apiRequest<LeadSaleConfigResponse>("/api/lead-sales/config");
  },
  async putLeadSalesConfig(config: LeadSaleConfig): Promise<LeadSaleConfig> {
    const r = await apiRequest<LeadSaleConfigEnvelope>("/api/lead-sales/config", {
      method: "PUT",
      body: config,
    });
    return r.config;
  },
  async setLeadSalesEnabled(enabled: boolean): Promise<LeadSaleConfig> {
    const r = await apiRequest<LeadSaleConfigEnvelope>("/api/lead-sales/enabled", {
      method: "PUT",
      body: { enabled },
    });
    return r.config;
  },
  leadSalesStats(opts?: {
    windowSec?: number;
    start?: number; // epoch SECONDS (existing TimeSel convention — see dialerStats)
    end?: number; // epoch SECONDS
  }): Promise<LeadSaleStats> {
    // Same window/range precedence as dialerStats: explicit start/end wins, else
    // a rolling window, else the server default (Pacific Today) when nothing is passed.
    const q = new URLSearchParams();
    if (opts?.start !== undefined && opts?.end !== undefined) {
      q.set("start", String(opts.start));
      q.set("end", String(opts.end));
    } else if (opts?.windowSec !== undefined) {
      q.set("window", String(opts.windowSec));
    }
    const qs = q.toString();
    return apiRequest<LeadSaleStats>(
      `/api/lead-sales/stats${qs ? `?${qs}` : ""}`,
    );
  },
  async leadSalesSales(limit?: number): Promise<LeadSaleRow[]> {
    const q = new URLSearchParams();
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    const r = await apiRequest<ListLeadSalesEnvelope>(
      `/api/lead-sales/sales${qs ? `?${qs}` : ""}`,
    );
    return r.entries;
  },
  async seenBuyers(): Promise<SeenBuyer[]> {
    const r = await apiRequest<SeenBuyersEnvelope>(
      "/api/lead-sales/seen-buyers",
    );
    return r.buyers;
  },

  // --- Witzzy (Witzzy-first sequential-fallback stage) ----------------------
  // config GET returns the whole WitzzyConfigResponse as-is (config +
  // tick_cap); the config PUT + enabled routes wrap the saved config in
  // { config } (unwrapped here). The config PUT body carries the WHOLE
  // WitzzyConfig incl. `enabled` (the server ignores the posted enabled and
  // preserves the stored value — the master toggle owns it) and
  // `destination.webhook_url_new` ONLY when the operator pasted a new URL.
  witzzyConfig(): Promise<WitzzyConfigResponse> {
    return apiRequest<WitzzyConfigResponse>("/api/lead-sales/witzzy/config");
  },
  async putWitzzyConfig(config: WitzzyConfig): Promise<WitzzyConfig> {
    const r = await apiRequest<WitzzyConfigEnvelope>(
      "/api/lead-sales/witzzy/config",
      { method: "PUT", body: config },
    );
    return r.config;
  },
  async setWitzzyEnabled(enabled: boolean): Promise<WitzzyConfig> {
    const r = await apiRequest<WitzzyConfigEnvelope>(
      "/api/lead-sales/witzzy/enabled",
      { method: "PUT", body: { enabled } },
    );
    return r.config;
  },
  witzzyStats(opts?: {
    windowSec?: number;
    start?: number; // epoch SECONDS (same TimeSel convention as leadSalesStats)
    end?: number; // epoch SECONDS
  }): Promise<WitzzyStats> {
    // Same window/range precedence as leadSalesStats: explicit start/end wins,
    // else a rolling window, else the server default (Pacific Today).
    const q = new URLSearchParams();
    if (opts?.start !== undefined && opts?.end !== undefined) {
      q.set("start", String(opts.start));
      q.set("end", String(opts.end));
    } else if (opts?.windowSec !== undefined) {
      q.set("window", String(opts.windowSec));
    }
    const qs = q.toString();
    return apiRequest<WitzzyStats>(
      `/api/lead-sales/witzzy/stats${qs ? `?${qs}` : ""}`,
    );
  },

  // --- HTM (Pipes.ai aged-lead resale stage) --------------------------------
  // config GET returns the whole HtmConfigResponse as-is (config + tick_cap +
  // min_age_ms + age_defaulted); the config PUT + flags routes wrap the saved
  // config in { config } (unwrapped here). The config PUT body is the
  // operator-editable HtmConfigUpdate ONLY — never enabled/test_mode (flags
  // owns them) and never the ARN/hint — with `api_key_new` ONLY when pasted.
  htmConfig(): Promise<HtmConfigResponse> {
    return apiRequest<HtmConfigResponse>("/api/lead-sales/htm/config");
  },
  async putHtmConfig(body: HtmConfigUpdate): Promise<HtmConfig> {
    const r = await apiRequest<HtmConfigEnvelope>(
      "/api/lead-sales/htm/config",
      { method: "PUT", body },
    );
    return r.config;
  },
  async setHtmFlags(flags: HtmFlagsUpdate): Promise<HtmConfig> {
    const r = await apiRequest<HtmConfigEnvelope>(
      "/api/lead-sales/htm/flags",
      { method: "PUT", body: flags },
    );
    return r.config;
  },
  htmStats(opts?: {
    windowSec?: number;
    start?: number; // epoch SECONDS
    end?: number; // epoch SECONDS
  }): Promise<HtmStats> {
    // Same precedence as witzzyStats: explicit start/end wins, else a rolling
    // window, else the server default (Pacific Today).
    const q = new URLSearchParams();
    if (opts?.start !== undefined && opts?.end !== undefined) {
      q.set("start", String(opts.start));
      q.set("end", String(opts.end));
    } else if (opts?.windowSec !== undefined) {
      q.set("window", String(opts.windowSec));
    }
    const qs = q.toString();
    return apiRequest<HtmStats>(
      `/api/lead-sales/htm/stats${qs ? `?${qs}` : ""}`,
    );
  },

  // --- Outbound calling ("posted → called → revenue" report) ---------------
  // Both routes are read-only GETs — see api.ts's Outbound calling section
  // above for the response shapes. `destination` defaults server-side to
  // "witzzy" when omitted; passed through only when the caller sets it.
  outboundCohorts(opts?: {
    days?: number; // 1..7, server default 7
    end?: number; // epoch SECONDS, server default now
    destination?: string; // server default "witzzy"
  }): Promise<OutboundCohortsResponse> {
    const q = new URLSearchParams();
    if (opts?.days !== undefined) q.set("days", String(opts.days));
    if (opts?.end !== undefined) q.set("end", String(opts.end));
    if (opts?.destination !== undefined) q.set("destination", opts.destination);
    const qs = q.toString();
    return apiRequest<OutboundCohortsResponse>(
      `/api/lead-sales/outbound/cohorts${qs ? `?${qs}` : ""}`,
    );
  },
  outboundLeads(params: {
    day: string; // "YYYY-MM-DD", required — one posting day per call
    limit?: number; // 1..200
    offset?: number; // >=0, integer offset (see OutboundLeadsResponse.next_offset)
    destination?: string; // server default "witzzy"
  }): Promise<OutboundLeadsResponse> {
    const q = new URLSearchParams({ day: params.day });
    if (params.limit !== undefined) q.set("limit", String(params.limit));
    if (params.offset !== undefined) q.set("offset", String(params.offset));
    if (params.destination !== undefined) q.set("destination", params.destination);
    return apiRequest<OutboundLeadsResponse>(
      `/api/lead-sales/outbound/leads?${q.toString()}`,
    );
  },

  // --- "Yes" conversational-iMessage engine ---------------------------------
  // config GET returns the whole YesConfigResponse as-is (config + tick_cap); the
  // config PUT + enabled routes wrap the saved config in { config } (unwrapped
  // here); conversations wraps the list in { count, entries } (unwrapped). The
  // config PUT body is the WHOLE YesConfig incl. `enabled` (the server ignores the
  // posted enabled and preserves the stored value — the master toggle owns it).
  yesConfig(): Promise<YesConfigResponse> {
    return apiRequest<YesConfigResponse>("/api/yes/config");
  },
  async putYesConfig(config: YesConfig): Promise<YesConfig> {
    const r = await apiRequest<YesConfigEnvelope>("/api/yes/config", {
      method: "PUT",
      body: config,
    });
    return r.config;
  },
  async setYesEnabled(enabled: boolean): Promise<YesConfig> {
    const r = await apiRequest<YesConfigEnvelope>("/api/yes/enabled", {
      method: "PUT",
      body: { enabled },
    });
    return r.config;
  },
  yesStats(opts?: {
    windowSec?: number;
    start?: number; // epoch SECONDS (existing TimeSel convention — see leadSalesStats)
    end?: number; // epoch SECONDS
  }): Promise<YesStats> {
    // Same window/range precedence as leadSalesStats: explicit start/end wins, else
    // a rolling window, else the server default (Pacific Today) when nothing is passed.
    const q = new URLSearchParams();
    if (opts?.start !== undefined && opts?.end !== undefined) {
      q.set("start", String(opts.start));
      q.set("end", String(opts.end));
    } else if (opts?.windowSec !== undefined) {
      q.set("window", String(opts.windowSec));
    }
    const qs = q.toString();
    return apiRequest<YesStats>(`/api/yes/stats${qs ? `?${qs}` : ""}`);
  },
  async yesConversations(limit?: number): Promise<YesConversationRow[]> {
    const q = new URLSearchParams();
    if (limit) q.set("limit", String(limit));
    const qs = q.toString();
    const r = await apiRequest<ListYesConversationsEnvelope>(
      `/api/yes/conversations${qs ? `?${qs}` : ""}`,
    );
    return r.entries;
  },

  // --- User Management + Identity (/api/me, admin-only /api/users) -----------
  me(): Promise<MeResponse> {
    return apiRequest<MeResponse>("/api/me");
  },
  async listUsers(): Promise<AdminUserRow[]> {
    const r = await apiRequest<ListUsersEnvelope>("/api/users");
    return r.users;
  },
  // Server returns { user, password } at the top level (password shown ONCE);
  // no envelope to unwrap — pass it through like createClient.
  createUser(input: CreateUserInput): Promise<CreateUserResponse> {
    return apiRequest<CreateUserResponse>("/api/users", {
      method: "POST",
      body: input,
    });
  },
  async putUserSections(
    sub: string,
    input: PutUserSectionsInput,
  ): Promise<AdminUserRow> {
    const r = await apiRequest<UserEnvelope>(
      `/api/users/${encodeURIComponent(sub)}/sections`,
      { method: "PUT", body: input },
    );
    return r.user;
  },
  async enableUser(sub: string): Promise<AdminUserRow> {
    const r = await apiRequest<UserEnvelope>(
      `/api/users/${encodeURIComponent(sub)}/enable`,
      { method: "POST" },
    );
    return r.user;
  },
  async disableUser(sub: string): Promise<AdminUserRow> {
    const r = await apiRequest<UserEnvelope>(
      `/api/users/${encodeURIComponent(sub)}/disable`,
      { method: "POST" },
    );
    return r.user;
  },
  // Server returns { password } at the top level (shown ONCE); no unwrap.
  resetUserPassword(sub: string): Promise<{ password: string }> {
    return apiRequest<{ password: string }>(
      `/api/users/${encodeURIComponent(sub)}/reset-password`,
      { method: "POST" },
    );
  },
};

export type Api = typeof api;
